import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faRotateRight,
  faArrowRight,
  faArrowLeft,
  faArrowDown,
  faLightbulb,
  faEye,
  faUpRightFromSquare,
  faChessKnight,
} from '@fortawesome/free-solid-svg-icons';
import AppContext from '../context/AppContext';
import CorrectionsAPI from '../api/CorrectionsAPI';
import { useAppDispatch } from '../store';
import { openPlayOut } from '../reducers/uiReducer';
import { analytics } from '../utils/analytics';
import { MoveType } from '../types/MoveType';
import { RepairQueue, RepairQueueItem } from '../types/CorrectionModel';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import {
  isGoodOrBetter,
  moveBetween,
  moveRoom,
  roomHintText,
  solutionPiece,
  uciToSan,
} from './correctionGrading';
import {
  awardForAttempt,
  formatMoney,
  previewSuccessCents,
  syncWalletFromServer,
} from '../utils/earnings';
import CorrectionBoard, { CorrectionOutcome, LinePosition } from './CorrectionBoard';
import RepairGamePanel from './RepairGamePanel';
import GoodMovesGauge from './GoodMovesGauge';

// Repairing mistakes across games. The server orders the queue by spaced repetition, so what comes
// next is what you're most likely to have forgotten. This is the drill only: RepairModal wraps it in
// the overlay and owns the scope and depth picker.
//
// Scroll the session back to the top of whatever is scrolling it (the modal body in the app, the
// page on /try).
function scrollSessionToTop(): void {
  window.setTimeout(() => {
    const session = document.querySelector('.cm-repair-session');
    if (!session) return;
    let node = session.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const scrolls =
        /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight;
      if (scrolls) {
        node.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      node = node.parentElement;
    }
    session.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 0);
}

export default function RepairSession(props: {
  gameIds: number[];
  username?: string | null;
  // Chosen by the wrapper; changing it remounts the session.
  minWeight: number;
  // Leaving the session (back to wherever it was launched from).
  onExit: () => void;
  // Opening the game a mistake came from, in full.
  onOpenGame: (gameId: number) => void;
}): React.ReactNode {
  const { gameIds, username, minWeight, onExit, onOpenGame } = props;
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const dispatch = useAppDispatch();

  const [queue, setQueue] = useState<RepairQueue | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [outcome, setOutcome] = useState<CorrectionOutcome | null>(null);
  // Bumped to remount the board, which is how "Try again" resets it.
  const [attemptKey, setAttemptKey] = useState(0);
  // Help asked for on this attempt:
  //   "Show my mistake": the move actually played, in words and as an arrow. Free.
  //   "Hint": which piece the engine moves, ringed. Most of the answer, so it halves the payout
  //      (see earnings.ts).
  const [showMistake, setShowMistake] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  // Play the move into the position before handing it over: on a new mistake yes, on "Try again" no
  // (you just watched it).
  const [playIntro, setPlayIntro] = useState(true);
  // Repairs recorded this session, so the counter updates without a refetch.
  const [repairedNow, setRepairedNow] = useState<Record<number, number>>({});
  // The moves list drives and follows the board: `boardNavRef` gets a jump function and `boardCursor`
  // tracks the position.
  const boardNavRef = useRef<((index: number) => void) | null>(null);
  // Where the payout chip flies from.
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [boardCursor, setBoardCursor] = useState(0);
  // Portal target for the board's "From here" row, under the verdict. State-backed so CorrectionBoard
  // re-renders once it mounts.
  const [bestLineSlot, setBestLineSlot] = useState<HTMLDivElement | null>(null);

  // (Re)load the queue whenever the scope or the depth changes.
  useEffect(() => {
    if (gameIds.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    CorrectionsAPI.getRepairQueue(gameIds, minWeight, username)
      .then((q) => {
        if (cancelled) return;
        setQueue(q);
        setIndex(0);
        setOutcome(null);
        setShowMistake(false);
        setHintUsed(false);
        setPlayIntro(true);
        setAttemptKey((k) => k + 1);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // gameIds is a fresh array each render and can be the whole library, so key on its shape rather than
    // joining thousands of ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minWeight, username, gameIds.length, gameIds[0], gameIds[gameIds.length - 1]]);

  const items = queue?.Items ?? [];
  const current: RepairQueueItem | undefined = items[index];

  // The queue isn't grouped by game, so "Game N of M" and prev/next game walk distinct games in order of
  // first appearance.
  const gameIdsInOrder = useMemo(() => {
    const seen = new Set<number>();
    const order: number[] = [];
    for (const it of items) {
      if (!seen.has(it.GameId)) {
        seen.add(it.GameId);
        order.push(it.GameId);
      }
    }
    return order;
  }, [items]);
  const gameFirstIndex = useMemo(() => {
    const map = new Map<number, number>();
    items.forEach((it, i) => {
      if (!map.has(it.GameId)) map.set(it.GameId, i);
    });
    return map;
  }, [items]);
  const currentGamePos = current != null ? gameIdsInOrder.indexOf(current.GameId) : -1;

  const goToGame = (pos: number) => {
    const gameId = gameIdsInOrder[pos];
    const target = gameId != null ? gameFirstIndex.get(gameId) : undefined;
    if (target == null) return;
    setIndex(target);
    setOutcome(null);
    setShowMistake(false);
    setHintUsed(false);
    setPlayIntro(true);
    setAttemptKey((k) => k + 1);
  };
  const goToPrevGame = () => goToGame(currentGamePos - 1);
  const goToNextGame = () => goToGame(currentGamePos + 1);

  // The solve position with its run-up, so the step controls can walk back into the game.
  const history = useMemo<LinePosition[]>(() => {
    if (current == null) return [];
    const fens = current.History.length > 0 ? current.History : [current.Fen];
    // Make sure the line ends on the solve position. Each position's last move is worked out from the
    // FEN before it, for the highlight and the intro move.
    const line = fens[fens.length - 1] === current.Fen ? fens : [...fens, current.Fen];
    return line.map((fen, i) => ({
      fen,
      lastMove: i === 0 ? undefined : (moveBetween(line[i - 1], fen) ?? undefined),
    }));
  }, [current]);

  // What the panel can say about the position: how much room it gave (free) and which piece the engine
  // moves (the paid hint). Either can be missing, and then it isn't offered.
  const room = useMemo(
    () =>
      current == null ? null : moveRoom(current.Fen, current.GoodMoveCount, current.LegalMoveCount),
    [current],
  );
  const pieceHint = useMemo(
    () => (current == null ? null : solutionPiece(current.Fen, current.Solution)),
    [current],
  );
  const record = (item: RepairQueueItem, result: CorrectionOutcome) => {
    setOutcome(result);

    // Money moves for guests too (theirs dies with the tab, see earnings.ts), so this runs before the
    // auth check. priorSuccesses is the server's time-discounted count plus repairs this session.
    const priorSuccesses =
      item.PayoutPriorSuccesses +
      ((repairedNow[item.MoveId] ?? item.RepairCount) - item.RepairCount);
    awardForAttempt({
      success: result.correct,
      swingPoints: item.PlayedSwing,
      priorSuccesses,
      hintUsed,
      // A wrong answer costs what this move gives up, not the original mistake. Null when ungraded; the
      // mistake's own swing then stands in.
      lossPoints: result.dropPct,
      anchor: boardWrapRef.current,
    });

    if (result.correct) {
      setRepairedNow((r) => ({ ...r, [item.MoveId]: (r[item.MoveId] ?? item.RepairCount) + 1 }));
    }

    // Before the auth check on purpose: attempts are only stored for signed-in users, and this is the
    // only trace a /try guest leaves.
    const uiSource = window.location.pathname.startsWith('/try') ? 'try' : 'app';
    analytics.track('repair_attempt', {
      ui_source: uiSource,
      solved: result.correct ? 'yes' : 'no',
      hint_used: hintUsed ? 'yes' : 'no',
      move_type: MoveType[item.MoveType],
    });
    if (result.correct) {
      analytics.track('repair_solved', { ui_source: uiSource, hint_used: hintUsed ? 'yes' : 'no' });
    }

    if (!isAuthenticated) return;
    CorrectionsAPI.recordAttempt(item.MoveId, item.GameId, result.correct, hintUsed, result.dropPct)
      .then(syncWalletFromServer)
      .catch(() => {});
  };

  const retry = () => {
    setOutcome(null);
    setShowMistake(false);
    setHintUsed(false);
    setPlayIntro(false);
    setAttemptKey((k) => k + 1);
  };

  const advance = () => {
    setIndex((i) => i + 1);
    setOutcome(null);
    setShowMistake(false);
    setHintUsed(false);
    setPlayIntro(true);
    setAttemptKey((k) => k + 1);
    // On a phone the next position would otherwise be off screen.
    scrollSessionToTop();
  };

  const close = () => {
    onExit();
  };

  // Play the mistake's position out against the engine, from the solve position: the question is
  // whether it was winnable at all.
  const playItOut = (item: RepairQueueItem) => {
    const opponent = item.ViewerColor === 'white' ? item.GameBlack : item.GameWhite;
    analytics.track('play_out_start', { ui_source: 'repair' });
    dispatch(
      openPlayOut({
        fen: item.Fen,
        color: item.SideToMove,
        evaluationCp: item.Evaluation,
        label: `${opponent ? `vs ${opponent} · ` : ''}move ${item.Number}`,
      }),
    );
  };

  const hasNext = index < items.length - 1;

  // On a phone the verdict sits under the board; scroll until its buttons are on screen, no further,
  // so the board stays in view. Desktop keeps the verdict beside the board.
  useEffect(() => {
    if (outcome == null || window.innerWidth >= 768) return;
    window.setTimeout(() => {
      const stop =
        document.querySelector('.cm-repair-session .cm-correct-result .cm-correct-actions') ??
        document.querySelector('.cm-repair-session .cm-correct-result');
      if (!stop) return;
      // Already on screen: leave the view alone.
      if (stop.getBoundingClientRect().bottom <= window.innerHeight) return;
      stop.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);
  }, [outcome]);

  return (
    <div className="cm-repair-session">
      <div className="cm-repair-session-body">
        {loading ? (
          <div className="cm-repair-empty">
            <Spinner animation="border" size="sm" className="me-2" />
            Building your queue…
          </div>
        ) : failed ? (
          <div className="cm-repair-empty">Couldn’t load the queue. Try again in a moment.</div>
        ) : items.length === 0 ? (
          <div className="cm-repair-empty">
            <p>
              Nothing to repair in these {queue?.GamesConsidered ?? gameIds.length} games at this
              depth.
            </p>
            <p className="text-muted">
              Pick a deeper setting above, widen the filter, or evaluate more games: a mistake can
              only be repaired once the engine has seen it.
            </p>
          </div>
        ) : current == null ? (
          // Walked off the end of the queue.
          <div className="cm-repair-empty">
            <p>
              <FontAwesomeIcon icon={faCheck} className="me-2 text-success" />
              That’s the queue: {items.length} {items.length === 1 ? 'mistake' : 'mistakes'}{' '}
              repaired across these games.
            </p>
            <p className="text-muted">
              Spaced repetition will bring the ones you got right back later, further apart each
              time you fix them.
            </p>
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <RepairContext
              item={current}
              repairCount={repairedNow[current.MoveId] ?? current.RepairCount}
              position={index + 1}
              total={items.length}
              dueCount={queue?.DueCount ?? 0}
              onOpenGame={() => onOpenGame(current.GameId)}
            />

            <div className="cm-repair-grid">
              <div className="cm-repair-board-col" ref={boardWrapRef}>
                <CorrectionBoard
                  key={`${current.MoveId}-${attemptKey}`}
                  history={history}
                  solveIndex={history.length - 1}
                  solution={current.Solution}
                  solveEvalCp={current.Evaluation}
                  sideToMove={current.SideToMove}
                  gamePlayedSan={current.PlayedMove}
                  gameMoveType={current.MoveType}
                  playIntro={playIntro}
                  showHint={outcome == null && showMistake}
                  pieceHintSquare={
                    outcome == null && hintUsed && pieceHint != null ? pieceHint.square : null
                  }
                  onResult={(result) => record(current, result)}
                  navRef={boardNavRef}
                  onNavChange={setBoardCursor}
                  bestLineSlot={bestLineSlot}
                />
              </div>

              {outcome == null ? (
                <div className={`cm-practice-await ${moveTypeClass(current.MoveType)}`}>
                  <div className="cm-practice-cue-row">
                    <MadeMistakeLine
                      sideToMove={current.SideToMove}
                      moveType={current.MoveType}
                      swing={current.PlayedSwing}
                      payoutCents={previewSuccessCents(
                        current.PlayedSwing,
                        current.PayoutPriorSuccesses +
                          ((repairedNow[current.MoveId] ?? current.RepairCount) -
                            current.RepairCount),
                        hintUsed,
                      )}
                      hintUsed={hintUsed}
                    />
                    {!showMistake ? (
                      <button
                        type="button"
                        className="cm-practice-hint-btn"
                        onClick={() => setShowMistake(true)}
                      >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        Show my mistake
                      </button>
                    ) : null}
                    {pieceHint != null && !hintUsed ? (
                      <button
                        type="button"
                        className="cm-practice-hint-btn is-costly"
                        onClick={() => setHintUsed(true)}
                        title="Shows which piece the engine moves — halves what this repair pays"
                      >
                        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                        Hint
                        <span className="cm-practice-hint-cost">half pay</span>
                      </button>
                    ) : null}
                  </div>
                  {/* Free help (room in the position) plus whatever was asked for, each line kept. */}
                  {showMistake || room != null || hintUsed ? (
                    <div className="cm-practice-hint-text">
                      {showMistake ? (
                        <div className="cm-practice-hint-step">
                          You played <strong className="cm-mono">{current.PlayedMove}</strong> here,
                          find a better move.
                        </div>
                      ) : null}
                      {room != null ? (
                        <div className="cm-practice-hint-step">{roomHintText(room)}</div>
                      ) : null}
                      {hintUsed && pieceHint != null ? (
                        <div className="cm-practice-hint-step">
                          The engine moves the {pieceHint.name} on{' '}
                          <strong className="cm-mono">{pieceHint.square}</strong>, ringed in blue.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <RepairReveal
                  item={current}
                  outcome={outcome}
                  hasNext={hasNext}
                  onRetry={retry}
                  onAdvance={advance}
                  onDone={close}
                  isAuthenticated={isAuthenticated}
                  openLogin={openLogin}
                  bestLineRef={setBestLineSlot}
                  onPlayItOut={() => playItOut(current)}
                />
              )}

              {/* The game around the drill, keyed so a new mistake starts clean. Beside the board on
                  desktop; below the prompt on mobile (.cm-repair-grid) so Try again / Next stay
                  above the fold. */}
              <RepairGamePanel
                key={current.MoveId}
                item={current}
                cursor={boardCursor}
                onGoTo={(i) => boardNavRef.current?.(i)}
                revealed={outcome != null}
              />
            </div>

            {/* Jump between the distinct games in the queue, to each game's first item. */}
            {gameIdsInOrder.length > 1 ? (
              <div className="cm-repair-game-nav">
                <button
                  type="button"
                  className="cm-repair-game-nav-btn"
                  onClick={goToPrevGame}
                  disabled={currentGamePos <= 0}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Previous game
                </button>
                <span className="cm-repair-game-nav-label">
                  Game {currentGamePos + 1} of {gameIdsInOrder.length}
                </span>
                <button
                  type="button"
                  className="cm-repair-game-nav-btn"
                  onClick={goToNextGame}
                  disabled={currentGamePos < 0 || currentGamePos >= gameIdsInOrder.length - 1}
                >
                  Next game
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

// Which game this mistake came from, how much repair history it carries, and where the session is.
function RepairContext(props: {
  item: RepairQueueItem;
  repairCount: number;
  position: number;
  total: number;
  dueCount: number;
  onOpenGame: () => void;
}): React.ReactNode {
  const { item, repairCount, position, total, dueCount, onOpenGame } = props;
  const date = new Date(item.GameDate);
  const dateLabel = Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
  // Both players in board order with the viewer marked.
  const white = item.GameWhite ?? '?';
  const black = item.GameBlack ?? '?';
  const viewerIsWhite = item.ViewerColor === 'white';
  return (
    <div className="cm-repair-context">
      <div className="cm-repair-context-game">
        <span className={`cm-name${viewerIsWhite ? ' cm-name-active' : ''}`}>{white}</span>
        <span className="text-muted"> vs </span>
        <span className={`cm-name${viewerIsWhite ? '' : ' cm-name-active'}`}>{black}</span>
        {dateLabel ? <span className="text-muted"> · {dateLabel}</span> : null}
        <span className="text-muted">
          {' '}
          · move {item.Number} · {item.GameMoveTotal} moves
          {item.GameTimeControl ? ` · ${item.GameTimeControl}` : ''}
        </span>
        <button type="button" className="cm-repair-open-game" onClick={onOpenGame}>
          <FontAwesomeIcon icon={faUpRightFromSquare} className="me-1" />
          See the full game
        </button>
      </div>
      <div className="cm-repair-context-meta">
        {/* Repair count, only once it's worth counting. */}
        {repairCount > 1 ? (
          <span className="cm-repair-count" title="Times you have repaired this move">
            <FontAwesomeIcon icon={faRotateRight} className="me-1" />
            repaired {repairCount}×
          </span>
        ) : null}
        {!item.IsDue ? (
          <span className="cm-repair-badge is-resting" title="Ahead of its next review">
            resting
          </span>
        ) : null}
        {/* "N due" only when some of the queue is resting. */}
        <span className="cm-repair-progress text-muted">
          {position} of {total}
          {dueCount > 0 && dueCount < total ? ` · ${dueCount} due` : ''}
        </span>
      </div>
    </div>
  );
}

// The graded result: your move classified, what to do next, and what the game move actually cost.
function RepairReveal(props: {
  item: RepairQueueItem;
  outcome: CorrectionOutcome;
  hasNext: boolean;
  onRetry: () => void;
  onAdvance: () => void;
  onDone: () => void;
  isAuthenticated: boolean;
  openLogin: () => void;
  // Where the board portals its "From here" best-line row.
  bestLineRef: (el: HTMLDivElement | null) => void;
  // Play the position out against the engine instead of moving on.
  onPlayItOut: () => void;
}): React.ReactNode {
  const {
    item,
    outcome,
    hasNext,
    onRetry,
    onAdvance,
    onDone,
    isAuthenticated,
    openLogin,
    bestLineRef,
    onPlayItOut,
  } = props;
  const playedSameMove = outcome.playedSan === item.PlayedMove;
  return (
    <div className={`cm-correct-result ${outcome.correct ? 'is-correct' : 'is-wrong'}`}>
      <div className="cm-correct-head-row">
        <div className="cm-cp-move-head cm-correct-eval">
          {outcome.moveType != null ? <MoveTypeReadout moveType={outcome.moveType} /> : null}
          {/* Only when win chance was actually lost. */}
          {outcome.dropPct != null &&
          outcome.dropPct > 0 &&
          (outcome.moveType == null || !isGoodOrBetter(outcome.moveType)) ? (
            <span
              className="cm-cp-move-swing down"
              title={`You gave up ${outcome.dropPct}% win chance`}
            >
              <FontAwesomeIcon icon={faArrowDown} className="me-1" />
              {outcome.dropPct}%
            </span>
          ) : null}
          {outcome.correct ? (
            <span className="cm-correct-result-head">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              {outcome.bestMoveSan != null && outcome.playedSan === outcome.bestMoveSan
                ? 'Correct. The engine’s move.'
                : 'That works. A good move or better.'}
            </span>
          ) : (
            <span className="cm-correct-result-head">
              {playedSameMove ? 'Same move as in the game.' : 'Not this one.'}
            </span>
          )}
          {playedSameMove ? (
            <span className="cm-correct-exact-note">this is the exact move you made</span>
          ) : null}
        </div>

        <div className="cm-correct-actions">
          {/* One primary action: Next after a repair, Try again after a miss. Play it out sits last:
              finding the move is one thing, still winning the position another. */}
          {(() => {
            const retryBtn = (
              <Button
                key="retry"
                variant={outcome.correct ? 'outline-secondary' : 'primary'}
                className={`cm-correct-btn${outcome.correct ? '' : ' is-primary'}`}
                onClick={onRetry}
              >
                <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                Try again
              </Button>
            );
            const nextBtn = hasNext ? (
              <Button
                key="next"
                variant={outcome.correct ? 'primary' : 'outline-secondary'}
                className={`cm-correct-btn${outcome.correct ? ' is-primary' : ''}`}
                onClick={onAdvance}
              >
                Next mistake
                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </Button>
            ) : (
              <Button
                key="done"
                variant={outcome.correct ? 'success' : 'outline-secondary'}
                className={`cm-correct-btn${outcome.correct ? ' is-primary' : ''}`}
                onClick={onDone}
              >
                Done
              </Button>
            );
            return outcome.correct ? [nextBtn, retryBtn] : [retryBtn, nextBtn];
          })()}
          <Button
            variant="outline-secondary"
            className="cm-correct-btn cm-playout-launch"
            onClick={onPlayItOut}
            title="Play this position out against the engine"
          >
            <FontAwesomeIcon icon={faChessKnight} className="me-2" />
            Play it out
          </Button>
        </div>
      </div>

      {/* The board's "From here" best-line row portals in here. */}
      <div className="cm-correct-bestline-slot" ref={bestLineRef} />

      <p className="cm-correct-result-detail">
        Best here was{' '}
        <strong>{outcome.bestMoveSan ?? uciToSan(item.Fen, item.Solution) ?? item.Solution}</strong>
        . You played <strong className="cm-mono">{item.PlayedMove}</strong>{' '}
        <span className={`cm-correct-detail-grade ${moveTypeClass(item.MoveType)}`}>
          {MoveType[item.MoveType]}
          {item.PlayedSwing > 0 ? (
            <>
              {' '}
              <FontAwesomeIcon icon={faArrowDown} className="me-1" />
              {item.PlayedSwing}%
            </>
          ) : null}
        </span>
      </p>

      {item.Commentary ? (
        <p className="cm-correct-result-note text-muted">{item.Commentary}</p>
      ) : null}

      {/* How much room the position gave you, as a gauge next to what you did with it. */}
      <GoodMovesGauge goodMoves={item.GoodMoveCount} legalMoves={item.LegalMoveCount} />

      {outcome.correct && !isAuthenticated ? (
        <div className="cm-correct-nudge">
          <span>Sign in to save your progress. Repairs only space out if they’re remembered.</span>
          <div className="cm-correct-nudge-actions">
            <Button size="sm" variant="primary" onClick={openLogin}>
              Sign in
            </Button>
            <button
              type="button"
              className="cm-correct-nudge-skip"
              onClick={hasNext ? onAdvance : onDone}
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// "Black to move. You made a blunder", like the in-game practice prompt.
function MadeMistakeLine(props: {
  sideToMove: 'white' | 'black';
  moveType: MoveType;
  // The mistake's swing and what repairing it pays now, shown before the attempt. The payout already
  // includes the repair ladder and the hint; `hintUsed` only marks it as discounted.
  swing?: number;
  payoutCents?: number;
  hintUsed?: boolean;
}): React.ReactNode {
  const Icon = getIcon(props.moveType);
  const name = MoveType[props.moveType];
  const article = /^[aeiou]/i.test(name) ? 'an' : 'a';
  const swing = props.swing ?? 0;
  return (
    <div className="cm-correct-prompt cm-practice-prompt">
      <div className="cm-practice-task">
        <strong>{props.sideToMove === 'white' ? 'White' : 'Black'} to move.</strong> Find the move
        you missed.
      </div>
      <div className="cm-practice-stake">
        In the game you made {article}{' '}
        <span
          className={`cm-correct-detail-grade cm-correct-prompt-grade ${moveTypeClass(props.moveType)}`}
        >
          <Icon className="cm-cp-move-icon" />
          {name.toLowerCase()}
        </span>
        {swing > 0 ? (
          <span className="cm-prompt-worth">
            <span className={`cm-prompt-swing ${moveTypeClass(props.moveType)}`}>
              &darr;{swing}%
            </span>
            {props.payoutCents ? (
              <span
                className={`cm-prompt-pay${props.hintUsed ? ' is-halved' : ''}`}
                title={props.hintUsed ? 'Halved because you took the hint' : undefined}
              >
                {formatMoney(props.payoutCents)}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// The move-class chip (icon + name), as in the game overlay's move header.
function MoveTypeReadout(props: { moveType: MoveType }): React.ReactNode {
  const Icon = getIcon(props.moveType);
  return (
    <span className={`cm-cp-move-type ${moveTypeClass(props.moveType)}`}>
      <Icon className="cm-cp-move-icon" />
      {MoveType[props.moveType]}
    </span>
  );
}
